import {
  IconBusinessMulticolor,
  IconCarMulticolor,
  IconChairMulticolor,
  IconHobbyMulticolor,
  IconHouseMulticolor,
  IconOtherMulticolor,
  IconPersonallyMulticolor,
  IconElectronicsMulticolor,
} from '@blocket/blocket-ui'
import pulse from '@blocket/tracking-pulse'
import { useDecision, withOptimizely } from '@optimizely/react-sdk'
import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { actionCreators as selectAdInsertionActionCreators } from 'reduxBits/selectAdInsertion/actions'
import Card from './Card'

const oldAIURL = '/ai'
const loginURL = '/login'
const recomAICategoryURL = (catID) => { return `/mina-annonser/annonsera/ny?categoryID=${catID}` }
const mobilityAIURL = '/mina-annonser/fordon/'
const pulsePageID = {
  'recomai': 'ai-draft-page',
  'oldai': 'old-ad-insertion',
  'bostad': 'blocketbostad',
  'fordon': 'view-pick-fordon-category'
}

const categoryNames = {
  1000: 'Fordon',
  2000: 'För hemmet',
  3000: 'Bostad',
  4000: 'Personligt',
  5000: 'Elektronik',
  6000: 'Fritid & hobby',
  7000: 'Affärsverksamhet',
  8000: 'Övrigt',
}

const sendClickEvent = (redirectUri, redirectDomain, categoryID) => {
  const isMobility = categoryID === 1000

  let vertical
  if (redirectDomain === 'recomai') {
    vertical = 'generalist'
  } else if (redirectDomain === 'bostad') {
    vertical = 'real_estate'
  } else if (isMobility) {
    vertical = 'mobility'
  }

  pulse.event({
    type: 'Click',
    name: 'Select main category',
    object: {
      id: 'main-category-selection',
      type: 'UIElement',
      elementType: 'Button',
      category: categoryNames[categoryID],
    },
    target: {
      id: pulsePageID[redirectDomain],
      type: 'Page',
      url: redirectUri,
    },
    vertical: {
      '@type': 'MarketplaceVertical',
      name: vertical
    }
  })
}

const mapDispatchToProps = (dispatch) => ({
  selectAdInsertionActions: bindActionCreators(selectAdInsertionActionCreators, dispatch),
})

const mapStateToProps = (state) => ({
  accountVerifiedByBankid: state.verification.status?.account?.verifiedByBankid,
  sessionVerifiedByBankid: state.verification.status?.session?.verifiedByBankid,
  userId: state.user.id,
})

const createCard = (id, icon, description = null) => {
  const CardComponent = ({
    selectAdInsertionActions,
    accountVerifiedByBankid,
    sessionVerifiedByBankid,
  }) => {
    const [decision] = useDecision('bank_id_verification_step2')
    return (
      <Card
        label={categoryNames[id]}
        description={description}
        price={0}
        icon={icon}
        onClick={(e) => {
          const redirectUri = recomAICategoryURL(id)
          sendClickEvent(redirectUri, 'recomai', id)
          if (decision.enabled && accountVerifiedByBankid && !sessionVerifiedByBankid) {
            selectAdInsertionActions.setVerificationModal(redirectUri)
            e.preventDefault()
          }
        }}
        link={recomAICategoryURL(id)}
      />
    )
  }
  CardComponent.propTypes = {
    selectAdInsertionActions: PropTypes.func,
    accountVerifiedByBankid: PropTypes.bool,
    sessionVerifiedByBankid: PropTypes.bool,
  }
  return CardComponent
}

const CarCardComponent = ({userId, optimizely}) => {
  let pulseId = 'oldai'
  let link = oldAIURL

  /**
   * In order to ensure users are bucketed correctly,
   * and that users get the same experience across all platforms,
   * we use the spid_id to check elegibiity.
   * To get a hold of the spid_id if users are not logged in,
   * redirect them to the login page.
   */
  if (userId) {
    const isFeatureEnabled = optimizely.isFeatureEnabled('mobility_new_ai', userId)

    if (isFeatureEnabled) {
      link = mobilityAIURL
      pulseId = 'fordon'
    }

  } else {
    const successUri = '/mina-annonser/lagg-in-annons'
    const cancelUri = '/mina-annonser/lagg-in-annons'

    link = `${loginURL}` +
    `?callbackId=desktop` +
    `&redirectUri=${encodeURIComponent(successUri)}` +
    `&cancelRedirectUri=${encodeURIComponent(cancelUri)}`
  }

  return (
    <Card
      label="Fordon"
      icon={<IconCarMulticolor />}
      onClick={() => sendClickEvent(link, pulseId, 1000)}
      link={link}
    />
  )
}

CarCardComponent.propTypes = {
  userId: PropTypes.string,
  optimizely: PropTypes.object,
}

export const HomeCard = connect(mapStateToProps, mapDispatchToProps)(
  createCard(2000, <IconChairMulticolor />)
)


// We will link directly to Qasa ad insertion
// Scheme that will be consumed by apps, in order to pickup the given url and open it inside a external web view.
const sendClickedQasaAdInsertionEvent = (url) => {
  pulse.event({
    type: 'Engagement',
    name: 'Clicked qasa ad category',
    object: {
      id: 'qasa-ad-insertion-link',
      type: 'UIElement',
      elementType: 'Button',
    },
    target: {
      id: 'blocketbostad',
      type: 'Page',
      url,
    },
    vertical: {
      name: "real_estate",
    }
  })
}

const blocketExternalScheme = `blocket-external`
const goToQasaAdInsertion = (platform) => {
  let url = 'https://bostad.blocket.se/sv/create-listing?rental-type=long-term&utm_source=blocket&utm_medium=web&utm_campaign=create-ad&utm_content=bostad'
  sendClickedQasaAdInsertionEvent('let', url)
  setTimeout(() => {
    const isWebView = platform === 'native-android' || platform === 'native-ios' 
    if (isWebView) {
      url = url.replace(/^https?/, blocketExternalScheme)
    }
      window.open(url, '_self')
  }, 300)
}

export const ResidenceCardNew = () => {
  let platform = ''
  if (typeof window !== "undefined") {
    platform = new URLSearchParams(window.location.search).get('platform')
  }

  return (
    <Card
      label="Bostad"
      description=""
      icon={<IconHouseMulticolor />}
      onClick={() => goToQasaAdInsertion(platform)}
      price="0"
    />
  )
}

export const PersonalCard = connect(mapStateToProps, mapDispatchToProps)(
  createCard(4000, <IconPersonallyMulticolor />, "T.ex. kläder, skor, barnmöbler eller leksaker")
)

export const ElectronicCard = connect(mapStateToProps, mapDispatchToProps)(
  createCard(5000, <IconElectronicsMulticolor />)
)

export const HobbyCard = connect(mapStateToProps, mapDispatchToProps)(
  createCard(6000, <IconHobbyMulticolor />, "T.ex. elcyklar, djur, musik- eller sportutrustning")
)

export const OtherCard = connect(mapStateToProps, mapDispatchToProps)(
  createCard(8000, <IconOtherMulticolor />)
)

export const CarCard = connect(mapStateToProps)(withOptimizely(CarCardComponent))

export const BusinessCard = () => {
  const [decision] = useDecision('recommerce_company_ai_for_apps')

  let affarsverksamhetTargetLink = oldAIURL
  if(decision.enabled) {
    if(decision.variables.affarsverksamhet_link_target) {
      affarsverksamhetTargetLink = decision.variables.affarsverksamhet_link_target
    }
  }
  return (
    <Card
      label={decision.enabled ? "Affärsverksamhet (företag)": "Affärsverksamhet"}
      description="T.ex. inventarier, maskiner, lokaler eller tjänster"
      icon={<IconBusinessMulticolor />}
      onClick={() => sendClickEvent(oldAIURL, 'oldai', 7000)}
      link={affarsverksamhetTargetLink}
    />
  )
}
